import { inject, Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';
import {
  NotificationConfig,
  NotificationType,
  NOTIFICATION_DEFAULT_DURATIONS,
  NOTIFICATION_TYPE_CLASSES,
  NOTIFICATION_DEFAULT_POSITION,
} from '../models/notification.models';

/**
 * Service for displaying toast notifications using Angular Material's MatSnackBar.
 * Provides typed convenience methods for success, error, warning, and info messages.
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly snackBar = inject(MatSnackBar);

  /**
   * Display a success notification.
   * @param message The message to display
   * @param config Optional configuration overrides
   */
  success(message: string, config?: NotificationConfig): MatSnackBarRef<TextOnlySnackBar> {
    return this.show(message, 'success', config);
  }

  /**
   * Display an error notification.
   * @param message The message to display
   * @param config Optional configuration overrides
   */
  error(message: string, config?: NotificationConfig): MatSnackBarRef<TextOnlySnackBar> {
    return this.show(message, 'error', config);
  }

  /**
   * Display a warning notification.
   * @param message The message to display
   * @param config Optional configuration overrides
   */
  warning(message: string, config?: NotificationConfig): MatSnackBarRef<TextOnlySnackBar> {
    return this.show(message, 'warning', config);
  }

  /**
   * Display an info notification.
   * @param message The message to display
   * @param config Optional configuration overrides
   */
  info(message: string, config?: NotificationConfig): MatSnackBarRef<TextOnlySnackBar> {
    return this.show(message, 'info', config);
  }

  /**
   * Dismiss the currently displayed notification.
   */
  dismiss(): void {
    this.snackBar.dismiss();
  }

  private show(
    message: string,
    type: NotificationType,
    config?: NotificationConfig,
  ): MatSnackBarRef<TextOnlySnackBar> {
    const duration = config?.duration ?? NOTIFICATION_DEFAULT_DURATIONS[type];
    const panelClass = config?.panelClass ?? NOTIFICATION_TYPE_CLASSES[type];
    const horizontalPosition = config?.horizontalPosition ?? NOTIFICATION_DEFAULT_POSITION.horizontal;
    const verticalPosition = config?.verticalPosition ?? NOTIFICATION_DEFAULT_POSITION.vertical;

    const snackBarConfig: MatSnackBarConfig = {
      duration,
      horizontalPosition,
      verticalPosition,
      panelClass,
    };

    if (config?.action) {
      return this.snackBar.open(message, config.action, snackBarConfig);
    }

    return this.snackBar.open(message, undefined, snackBarConfig);
  }
}
